import React, { useState, useMemo, useEffect } from "react"
import Breadcrumb from "components/Common/Breadcrumb"
import { Card, CardBody, Table, Input, Button } from "reactstrap"
import { Switch } from "antd"
import { FaEdit, FaEye, FaPlus } from "react-icons/fa"
import { MdDelete } from "react-icons/md"
import StaffModal from "./StaffModal"
import Swal from "sweetalert2"
import Loader from "components/Loader/Loader"
import {
  fetchData,
  FetchDeleteData,
  FetchPostData,
  FetchPutData,
} from "utils/Common_api_functions"
import Configs from "../../config"
import moment from "moment"
import PermittedComponent from "PermittedComponent"
import { usePermissionData } from "AppContext"

const StaffIndex = () => {
  document.title = "Staff | Vintage Infra Equipment Sol PVT LTD"

  // pagination + search
  const [pageSize, setPageSize] = useState(20)
  const [searchString, setSearchString] = useState("")

  const [loading, setLoading] = useState(false)
  // staff data
  const [staffData, setStaffData] = useState([])
  const [roles, setRoles] = useState([])

  useEffect(() => {
    getStaffDetails()
    fetchRoles()
  }, [])

  async function getStaffDetails() {
    const APIdata = await fetchData(setLoading, "users")
    if (APIdata?.success) {
      setStaffData(APIdata?.data?.reverse())
    }
  }

  async function fetchRoles() {
    try {
      const APIdata = await fetchData(setLoading, "roles")
      if (APIdata?.success) {
        setRoles(
          APIdata?.data
            ?.reverse()
            .map(item => ({ id: item.id, name: item.roleName }))
        )
      }
    } catch (error) {
      console.log(error)
    }
  }

  // toggle status
  const toggleStatus = async (id, checked) => {
    Swal.fire({
      title: `Are you sure you want to ${
        checked ? "Enable" : "Disable"
      } this User?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then(async result => {
      if (result.isConfirmed) {
        const APIdata = await FetchPutData(setLoading, "users", id, {
          status: checked,
        })
        if (APIdata?.data?.success) {
          Swal.fire({
            title: `Staff ${checked ? "Enabled" : "Disabled"}  Successfully.`,
            text: APIdata?.data?.message,
            icon: "success",
          })
          getStaffDetails()
          setSelectedStaff({})
        } else {
          Swal.fire({
            title: "Error Updating Staff.",
            text: APIdata?.response?.data?.message,
            icon: "error",
          })
        }
      }
    })
  }

  // filtered data
  const filteredData = useMemo(() => {
    return staffData.filter(
      item =>
        item?.name?.toLowerCase().includes(searchString.toLowerCase()) ||
        item?.email?.toLowerCase().includes(searchString.toLowerCase()) ||
        item?.phone?.toLowerCase().includes(searchString.toLowerCase()) ||
        item?.employeeId?.toLowerCase().includes(searchString.toLowerCase()) ||
        item?.designation?.toLowerCase().includes(searchString.toLowerCase())
    )
  }, [staffData, searchString])

  // Pagination
  const [currentPage, setCurrentPage] = useState(1)

  const totalPages = Math.ceil(filteredData.length / pageSize)
  const start = (currentPage - 1) * pageSize

  const paginatedData = useMemo(() => {
    const end = start + pageSize
    return filteredData.slice(start, end)
  }, [filteredData, currentPage, pageSize])

  // Add Edit Modal
  const [modalOpen, setModalOpen] = useState(false)
  const [selectedStaff, setSelectedStaff] = useState({})
  const [viewModal, setViewModal] = useState(false)

  const handleSubmit = async () => {
    if (selectedStaff?.id) {
      const APIdata = await FetchPutData(
        setLoading,
        "users",
        selectedStaff?.id,
        selectedStaff
      )
      if (APIdata?.data?.success) {
        Swal.fire({
          title: "Staff Updated Successfully.",
          text: APIdata?.data?.message,
          icon: "success",
        })
        setModalOpen(false)
        getStaffDetails()
        setSelectedStaff({})
      } else {
        Swal.fire({
          title: "Error Updating Staff.",
          text: APIdata?.response?.data?.message,
          icon: "error",
        })
      }
    } else {
      const APIdata = await FetchPostData(setLoading, "users", selectedStaff)
      if (APIdata?.data?.success) {
        Swal.fire({
          title: "Staff Added Successfully.",
          text: APIdata?.data?.message,
          icon: "success",
        })
        setModalOpen(false)
        setSelectedStaff({})
        getStaffDetails()
      } else {
        Swal.fire({
          title: "Error Adding Staff.",
          text: APIdata?.response?.data?.message,
          icon: "error",
        })
      }
    }
  }

  const handleDelete = (staff, id) => {
    Swal.fire({
      title: `Are you sure you want to delete ${staff?.name}?`,
      text: "This action cannot be undone!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then(async result => {
      if (result.isConfirmed) {
        const APIdata = await FetchDeleteData(setLoading, "users", id)
        if (APIdata?.data?.success) {
          Swal.fire({
            title: "Staff Deleted Successfully.",
            text: APIdata?.data?.message,
            icon: "success",
          })
          getStaffDetails()
        } else {
          Swal.fire({
            title: "Error Deleting Staff.",
            text: APIdata?.response?.data?.message,
            icon: "error",
          })
        }
      }
    })
  }

  const { checkAccess } = usePermissionData()

  return (
    <PermittedComponent isAccess={checkAccess(1, "view")}>
      <div className="page-content">
        {loading && <Loader />}
        <Breadcrumb title="Dashboard" breadcrumbItem="Staff" />

        <Card className="shadow-lg">
          <CardBody>
            {/* Top controls */}
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div className="d-flex gap-3">
                <Input
                  type="select"
                  value={pageSize}
                  onChange={e => {
                    setPageSize(Number(e.target.value)), setCurrentPage(1)
                  }}
                  className="rounded-4 w-auto pe-5"
                >
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </Input>

                <Input
                  className="rounded-4"
                  placeholder="Search Staff"
                  value={searchString}
                  onChange={e => setSearchString(e.target.value)}
                />
              </div>
              {checkAccess(1, "add") && (
                <Button
                  className="btn-blue rounded-4 px-3"
                  onClick={() => {
                    setSelectedStaff({}),
                      setViewModal(false),
                      setModalOpen(true)
                  }}
                >
                  <FaPlus className="me-2" />
                  Staff
                </Button>
              )}
            </div>

            {/* Table */}
            <Table bordered responsive>
              <thead className="table-dark">
                <tr>
                  <th>#</th>
                  <th>Emp ID</th>
                  <th>Name</th>
                  <th>Designation</th>
                  <th>Email</th>
                  <th>Contact No</th>
                  <th>Joining Date</th>
                  <th>Status</th>
                  <th className="text-center">Action</th>
                </tr>
              </thead>

              <tbody>
                {paginatedData.length === 0 ? (
                  <tr>
                    <td colSpan="9" className="text-center">
                      No staff found
                    </td>
                  </tr>
                ) : (
                  paginatedData.map((item, index) => (
                    <tr key={item.id}>
                      <td>{start + index + 1}</td>

                      <td>{item?.employeeId}</td>
                      <td className="d-flex align-items-center gap-3">
                        <img
                          src={`${Configs.imageUrl}${item.profilePicture}`} // <-- Make sure your data has an 'image' URL property
                          width={40}
                          height={40}
                          style={{ borderRadius: "50%", objectFit: "cover" }}
                        />
                        {item.name}
                      </td>
                      <td>{item?.designation}</td>
                      <td>{item.email}</td>
                      <td>{item.phone}</td>
                      <td>{moment(item.joiningDate).format("DD-MM-YYYY")}</td>
                      <td>
                        <Switch
                          checked={item.status}
                          onChange={checked => toggleStatus(item.id, checked)}
                        />
                      </td>
                      <td style={{ whiteSpace: "nowrap" }}>
                        <Button
                          className="bg-secondary text-white me-2 border-0 shadow-none"
                          onClick={() => {
                            setSelectedStaff(item)
                            setRoles(roles)
                            setViewModal(true)
                            setModalOpen(true)
                          }}
                        >
                          <FaEye size={16} />
                        </Button>

                        {checkAccess(1, "edit") && (
                          <Button
                            className="bg-success text-white me-2 border-0 shadow-none"
                            onClick={() => {
                              const cleanStaff = { ...item }
                              delete cleanStaff.password

                              setSelectedStaff(cleanStaff)
                              setViewModal(false)
                              setModalOpen(true)
                            }}
                          >
                            <FaEdit size={16} />
                          </Button>
                        )}

                        {checkAccess(1, "delete") && (
                          <Button
                            className="bg-danger text-white border-0 shadow-none"
                            onClick={() => handleDelete(item, item?.id)}
                          >
                            <MdDelete size={18} />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
            <nav
              aria-label="Page navigation example"
              className="d-flex justify-content-between mt-3"
            >
              <h5>
                Showing {start + 1} to{" "}
                {Math.min(start + pageSize, filteredData.length)} of{" "}
                {staffData.length} entries
              </h5>
              <ul className="pagination mb-0">
                <li
                  className={`page-item ${currentPage === 1 ? "disabled" : ""}`}
                >
                  <button
                    className="page-link"
                    onClick={() =>
                      setCurrentPage(prev => Math.max(prev - 1, 1))
                    }
                  >
                    Previous
                  </button>
                </li>

                {[...Array(totalPages)].map((_, i) => {
                  const pageNum = i + 1
                  return (
                    <li
                      key={pageNum}
                      className={`page-item ${
                        pageNum === currentPage ? "active" : ""
                      }`}
                    >
                      <button
                        className="page-link"
                        onClick={() => setCurrentPage(pageNum)}
                      >
                        {pageNum}
                      </button>
                    </li>
                  )
                })}

                <li
                  className={`page-item ${
                    currentPage === totalPages ? "disabled" : ""
                  }`}
                >
                  <button
                    className="page-link"
                    onClick={() =>
                      setCurrentPage(prev => Math.min(prev + 1, totalPages))
                    }
                  >
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          </CardBody>
        </Card>

        <StaffModal
          open={modalOpen}
          toggle={() => setModalOpen(false)}
          selectedStaff={selectedStaff}
          roles={roles}
          setSelectedStaff={setSelectedStaff}
          viewOnly={viewModal}
          onSubmit={handleSubmit}
        ></StaffModal>
      </div>
    </PermittedComponent>
  )
}

export default StaffIndex
